import { useEffect, useState } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { SERVICE_DESK } from '@atlassian/jira-shared-types/src/application-key.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import { fetchEntitlementDetails } from '../../common/utils/index.tsx';
import { EDITION_KEY, ADMIN_HUB_URL } from './constants.tsx';

const getCcpTransactionAccountId = async (entitlementId: string) => {
	const entitlementDetails = await fetchEntitlementDetails(entitlementId);

	if (!entitlementDetails?.transactionAccountId) {
		throw Error('Could not get transactionAccountId');
	}
	return (entitlementDetails ?? {}).transactionAccountId;
};

export const useGetJsmPremiumBillingUrl = () => {
	const [billingPageUrl, setBillingPageUrl] = useState<string | undefined>();
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | unknown | null>(null);

	const tenantContext = useTenantContext();
	const environment = useEnvironment();
	const { data: orgId } = useOrgId();

	useEffect(() => {
		const loadBillingInfo = async () => {
			try {
				const productKey = SERVICE_DESK;
				const isUsingCcp =
					tenantContext.productEntitlementDetails?.[productKey]?.billingSourceSystem === 'CCP';
				const referrerKey = 'jsm';
				const commerceProductKey = `${productKey}.ondemand`;
				const entitlementId = tenantContext.productEntitlementDetails?.[productKey]?.entitlementId;
				const edition = EDITION_KEY.PREMIUM_EDITION;
				const adminHubBaseUrl = ADMIN_HUB_URL[environment];

				if (isUsingCcp) {
					// CCP Billing System
					if (!entitlementId) {
						throw new Error('Empty entitlement ID');
					}

					const ccpTransactionAccountId = await getCcpTransactionAccountId(entitlementId);

					const ccpBaseUrl = fg('benifits_modal_billing_page_url_change')
						? `${adminHubBaseUrl}/billing/${ccpTransactionAccountId}/entitlement/${entitlementId}/change-plan?referrer=${referrerKey}`
						: `${adminHubBaseUrl}/billing/o/${orgId}/${ccpTransactionAccountId}/entitlement/${entitlementId}/change-plan?referrer=${referrerKey}`;

					setBillingPageUrl(`${ccpBaseUrl}&offeringName=${edition}`);
				} else {
					// HAMS Billing System
					setBillingPageUrl(
						`${adminHubBaseUrl}/s/${tenantContext.cloudId}/billing/applications/change-edition/${commerceProductKey}/${edition}`,
					);
				}
			} catch (e) {
				setError(e);
				fireErrorAnalytics({
					meta: {
						id: 'useGetJsmPremiumBillingUrl',
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					error: e as Error,
					sendToPrivacyUnsafeSplunk: true,
				});
			} finally {
				setLoading(false);
			}
		};

		loadBillingInfo();
	}, [environment, orgId, tenantContext, tenantContext.cloudId]);

	return {
		data: {
			billingPageUrl,
		},
		loading,
		error,
	};
};
